import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

/**
 * AnimatedAvatar — Pixar-style avatar of Sabril (founder) with dynamic speech bubbles
 *
 * Props:
 *  - message: string | string[]  Dynamic message(s). If array, cycles through them.
 *  - position: 'left' | 'right'  Side of the bubble (default 'right' = bubble to the right of avatar)
 *  - size: 'sm' | 'md' | 'lg' | 'xl'  Avatar size
 *  - pointing: boolean  Tilt + slight rotation as if pointing at content
 *  - pulse: boolean  Add a subtle orange glow halo (CTA emphasis)
 *  - delay: number  Animation reveal delay (sec)
 *  - testid: string  data-testid for the wrapper
 *  - bubbleClassName: extra classes for the bubble
 *  - bubbleArrow: 'left' | 'right' | 'bottom' | null
 *  - caption: string  Optional caption under avatar ("Sabril · Fondateur PRSNL")
 *  - signed: boolean  Show "— Sabril" signature in bubble
 */
const AnimatedAvatar = ({
  message,
  position = 'right',
  size = 'md',
  pointing = false,
  pulse = false,
  delay = 0,
  testid = 'animated-avatar',
  bubbleClassName = '',
  bubbleArrow = null,
  caption = null,
  signed = true,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const messages = Array.isArray(message) ? message : message ? [message] : [];
  const [msgIndex, setMsgIndex] = useState(0);

  // Cycle messages every 4.5s if there are multiple
  useEffect(() => {
    if (messages.length < 2) return;
    const id = setInterval(() => {
      setMsgIndex((i) => (i + 1) % messages.length);
    }, 4500);
    return () => clearInterval(id);
  }, [messages.length]);

  const sizeMap = {
    sm: { box: 'w-16 h-16', avatar: 'w-16 h-16' },
    md: { box: 'w-24 h-24', avatar: 'w-24 h-24' },
    lg: { box: 'w-32 h-32', avatar: 'w-32 h-32' },
    xl: { box: 'w-40 h-40 sm:w-48 sm:h-48', avatar: 'w-40 h-40 sm:w-48 sm:h-48' },
  };
  const { box, avatar } = sizeMap[size] || sizeMap.md;

  const bubbleSide = position === 'left' ? 'order-2 sm:order-2' : 'order-2';
  const avatarOrder = position === 'left' ? 'order-1' : 'order-1';
  const flexDir = position === 'left' ? 'sm:flex-row-reverse' : 'sm:flex-row';

  const currentMessage = messages[msgIndex];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18, scale: 0.9 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.55, delay, type: 'spring', stiffness: 110, damping: 14 }}
      className={`flex flex-col items-center sm:items-end gap-3 ${flexDir} sm:gap-4`}
      data-testid={testid}
    >
      {/* AVATAR */}
      <div className={`relative ${avatarOrder} flex-shrink-0`}>
        {/* Pulsing halo (CTA emphasis) */}
        {pulse && (
          <motion.div
            className="absolute inset-0 rounded-full bg-[#F05A1A]/40 blur-2xl"
            animate={{ scale: [1, 1.25, 1], opacity: [0.45, 0.15, 0.45] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          />
        )}

        {/* Soft orange ring */}
        <div
          className="absolute -inset-1 rounded-full pointer-events-none"
          style={{
            background:
              'conic-gradient(from 90deg, rgba(240,90,26,0.55), rgba(251,191,36,0.25), rgba(240,90,26,0.55))',
            filter: 'blur(6px)',
          }}
          aria-hidden="true"
        />

        {/* Floating + breathing + optional pointing tilt */}
        <motion.div
          className={`relative ${box}`}
          animate={
            pointing
              ? { y: [0, -6, 0], rotate: [-3, 2, -3] }
              : { y: [0, -6, 0] }
          }
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png"
            alt="Sabril — Fondateur PRSNL"
            className={`${avatar} object-cover rounded-full border-[3px] border-white shadow-[0_10px_30px_rgba(240,90,26,0.35)] bg-white`}
            draggable={false}
          />

          {/* Status dot */}
          <span
            className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow"
            aria-hidden="true"
          />

          {/* Pointing finger indicator (only when pointing) */}
          {pointing && (
            <motion.div
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-2xl"
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            >
              👇
            </motion.div>
          )}
        </motion.div>

        {caption && (
          <p className="mt-2 text-center text-[10px] font-black uppercase tracking-widest text-[#F05A1A]">
            {caption}
          </p>
        )}
      </div>

      {/* SPEECH BUBBLE */}
      {currentMessage && (
        <div className={`relative ${bubbleSide} max-w-[280px] sm:max-w-sm`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={msgIndex}
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.35 }}
              className={`relative bg-white text-[#1a1a1a] rounded-2xl px-5 py-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-[#e0dbd4] ${bubbleClassName}`}
            >
              {/* Bubble tail */}
              {bubbleArrow !== null && (
                <span
                  className="absolute w-3 h-3 bg-white border-[#e0dbd4]"
                  style={
                    bubbleArrow === 'left'
                      ? {
                          left: '-6px',
                          top: '50%',
                          transform: 'translateY(-50%) rotate(45deg)',
                          borderLeft: '1px solid #e0dbd4',
                          borderBottom: '1px solid #e0dbd4',
                        }
                      : bubbleArrow === 'right'
                      ? {
                          right: '-6px',
                          top: '50%',
                          transform: 'translateY(-50%) rotate(45deg)',
                          borderRight: '1px solid #e0dbd4',
                          borderTop: '1px solid #e0dbd4',
                        }
                      : {
                          bottom: '-6px',
                          left: '24px',
                          transform: 'rotate(45deg)',
                          borderRight: '1px solid #e0dbd4',
                          borderBottom: '1px solid #e0dbd4',
                        }
                  }
                  aria-hidden="true"
                />
              )}
              <p className="text-sm leading-snug font-medium" data-testid="avatar-bubble-text">
                {currentMessage}
              </p>
              {signed && (
                <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-[#F05A1A]">
                  — Sabril · Fondateur PRSNL
                </p>
              )}

              {/* Dots indicator for multi-message */}
              {messages.length > 1 && (
                <div className="flex gap-1 mt-2">
                  {messages.map((_, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        i === msgIndex ? 'bg-[#F05A1A] w-3' : 'bg-[#e0dbd4]'
                      }`}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

/**
 * FloatingAvatarWidget — fixed bottom-right "chat support" style with an interactive 3-question
 * mini-qualifier that routes to the right offer + pre-fills a WhatsApp message.
 *
 * Props:
 *  - autoOpenDelayMs: number   Délai avant auto-ouverture (default 6000ms). Set null pour désactiver.
 *  - autoOpenScrollPastSelector: string  Sélecteur CSS d'une section dont la sortie de viewport
 *      (scroll past) déclenche l'auto-ouverture. Si fourni, l'auto-ouverture par temps est désactivée.
 *  - simpleMode: boolean  Désactive le qualifieur, propose juste un message WhatsApp humain.
 *      À utiliser sur les pages où on ne veut pas pitcher d'offre (ex: fin d'audit).
 *  - simpleHeadline: string  Le texte d'intro affiché en mode simple.
 *  - simpleWhatsappText: string  Le message WhatsApp pré-rempli en mode simple.
 */
export const FloatingAvatarWidget = ({
  testid = 'floating-avatar-widget',
  autoOpenDelayMs = 6000,
  autoOpenScrollPastSelector = null,
  simpleMode = false,
  simpleHeadline = "Une question ? J'ai 15 min pour toi sur WhatsApp.",
  simpleWhatsappText = "Salut Sabril, j'ai une question.",
}) => {
  const [open, setOpen] = useState(false);
  const [hasShownInitial, setHasShownInitial] = useState(false);
  const [step, setStep] = useState(0); // 0 = intro, 1 = ca, 2 = goal, 3 = urgency, 4 = recommendation
  const [answers, setAnswers] = useState({ ca: null, goal: null, urgency: null });

  // Auto-open après délai (désactivé si scroll-past trigger fourni)
  useEffect(() => {
    if (hasShownInitial || autoOpenScrollPastSelector || autoOpenDelayMs == null) return;
    const id = setTimeout(() => {
      setOpen(true);
      setHasShownInitial(true);
    }, autoOpenDelayMs);
    return () => clearTimeout(id);
  }, [hasShownInitial, autoOpenScrollPastSelector, autoOpenDelayMs]);

  // Auto-open quand la section cible sort de la viewport vers le haut (scroll past)
  useEffect(() => {
    if (hasShownInitial || !autoOpenScrollPastSelector) return;
    const el = document.querySelector(autoOpenScrollPastSelector);
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          // Section au-dessus de la viewport (l'utilisateur l'a dépassée)
          if (!entry.isIntersecting && rect.bottom < 0) {
            setOpen(true);
            setHasShownInitial(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasShownInitial, autoOpenScrollPastSelector]);

  const pick = (field, value) => {
    setAnswers((a) => ({ ...a, [field]: value }));
    setStep((s) => s + 1);
  };

  // Routing logic — recommends the right offer based on answers
  const recommendation = (() => {
    const { ca, goal, urgency } = answers;
    if (!ca || !goal || !urgency) return null;

    // < 20k€/mois OR "tester avant" → 1 Day OR Plan 47€
    if (ca === '<20k' && urgency === 'tester') {
      return {
        title: 'Plan d\'exécution 47€',
        subtitle: 'Le plus pertinent pour toi à ce stade',
        reason: 'Tu es en phase de structuration. Avant d\'investir en coaching, le plan d\'exécution 30 jours à 47€ te donne les bases.',
        href: '/ebook',
        ctaLabel: 'Voir le plan 47€',
        ctaIcon: '📘',
        target: '_self',
      };
    }
    if (ca === '<20k') {
      return {
        title: '1 Day — Audit terrain',
        subtitle: '2 000€ · 1 journée sur place',
        reason: 'Ton CA n\'est pas encore au niveau Refonte. Un audit terrain 1 journée te donnera un plan concret + 30j de suivi.',
        href: '#pricing-tabs-anchor',
        ctaLabel: 'Voir l\'offre 1 Day',
        ctaIcon: '🎯',
        target: '_self',
        tab: 'audit_day',
      };
    }

    // Ouverture
    if (goal === 'ouvrir') {
      return {
        title: 'Ouverture — 3 mois',
        subtitle: '14 900€ · 9 jours sur place',
        reason: 'Tu ouvres un nouveau resto. Le forfait Ouverture est fait pour toi : concept → D-Day → 30j post-ouverture.',
        href: '#pricing-tabs-anchor',
        ctaLabel: 'Voir Ouverture',
        ctaIcon: '🚀',
        target: '_self',
        tab: 'ouverture',
      };
    }

    // Groupe (multi-resto)
    if (goal === 'scaler') {
      return {
        title: 'Groupe — 6 mois',
        subtitle: '29 000€ · 12 jours sur place répartis',
        reason: 'Tu as plusieurs établissements. L\'accompagnement Groupe est conçu pour structurer et scaler proprement.',
        href: '#pricing-tabs-anchor',
        ctaLabel: 'Voir Groupe',
        ctaIcon: '🏢',
        target: '_self',
        tab: 'groupe',
      };
    }

    // Tester avant gros engagement → 1 Day
    if (urgency === 'tester') {
      return {
        title: '1 Day — Audit terrain',
        subtitle: '2 000€ · 1 journée sur place',
        reason: 'Tu veux tester avant de t\'engager. Le 1 Day est parfait : audit terrain + plan + débrief J+30. Et si tu prends Refonte, les 2 000€ sont déduits.',
        href: '#pricing-tabs-anchor',
        ctaLabel: 'Voir 1 Day',
        ctaIcon: '🎯',
        target: '_self',
        tab: 'audit_day',
      };
    }

    // Default: Refonte
    return {
      title: 'Refonte — 90 jours',
      subtitle: '6 700€ · 2 jours sur place',
      reason: 'Ton resto tourne. L\'objectif est d\'optimiser et structurer. Refonte 90 jours est l\'offre la plus demandée pour ton profil.',
      href: '#pricing-tabs-anchor',
      ctaLabel: 'Voir Refonte',
      ctaIcon: '🔥',
      target: '_self',
      tab: 'refonte',
    };
  })();

  const handleRecommendationClick = (rec) => {
    if (rec.target === '_self' && rec.href.startsWith('#')) {
      // Scroll to pricing & switch tab
      const target = document.querySelector('[data-testid="pricing-tabs"]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (rec.tab) {
          // Defer to let scroll happen first
          setTimeout(() => {
            const tabBtn = document.querySelector(`[data-testid="pricing-tab-${rec.tab}"]`);
            if (tabBtn) tabBtn.click();
          }, 600);
        }
        setOpen(false);
      } else {
        // Pas de pricing-tabs sur cette page (ex: /resultats) — redirection vers /prsnl
        const tab = rec.tab || 'ouverture';
        window.location.href = `/prsnl?tab=${tab}`;
      }
    } else if (rec.target === '_self') {
      window.location.href = rec.href;
    }
  };

  const buildWhatsappLink = () => {
    const caLabels = { '<20k': 'moins de 20 000€/mois', '20-50k': '20-50 000€/mois', '50-100k': '50-100 000€/mois', '100k+': 'plus de 100 000€/mois' };
    const goalLabels = { ouvrir: 'ouvrir un nouveau resto', optimiser: 'optimiser ma rentabilité', scaler: 'scaler en multi-resto / groupe' };
    const urgencyLabels = { now: 'je veux démarrer rapidement', '3mois': 'd\'ici 3 mois', tester: 'tester avant de m\'engager' };
    const msg = [
      `Salut Sabril, j'ai utilisé le qualifieur PRSNL :`,
      `· CA : ${caLabels[answers.ca] || answers.ca}`,
      `· Objectif : ${goalLabels[answers.goal] || answers.goal}`,
      `· Timing : ${urgencyLabels[answers.urgency] || answers.urgency}`,
      recommendation ? `· L'algo me recommande : ${recommendation.title}` : '',
      `J'aimerais en parler avec toi.`,
    ].filter(Boolean).join('\n');
    return `https://wa.me/33745522450?text=${encodeURIComponent(msg)}`;
  };

  const reset = () => {
    setStep(0);
    setAnswers({ ca: null, goal: null, urgency: null });
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2"
      data-testid={testid}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.92 }}
            transition={{ duration: 0.28 }}
            className="relative bg-white rounded-2xl shadow-2xl border border-[#e0dbd4] p-4 w-[300px] sm:w-[340px]"
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#1a1a1a] text-white text-xs flex items-center justify-center shadow"
              aria-label="Fermer"
              data-testid="floating-avatar-close"
            >
              ✕
            </button>

            {/* HEADER */}
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#e0dbd4]">
              <div className="relative w-10 h-10 flex-shrink-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png"
                  alt="Sabril"
                  className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] leading-tight">Sabril · en ligne</p>
                <p className="text-[11px] text-[#1a1a1a]/60 leading-tight mt-0.5">
                  {simpleMode ? 'Réponse sous 15 min' : (step === 0 ? '2 min pour trouver ton offre' : step < 4 ? `Question ${step}/3` : 'Recommandation prête')}
                </p>
              </div>
            </div>

            {/* SIMPLE MODE — WhatsApp direct, sans qualifieur */}
            {simpleMode && (
              <motion.div
                key="simple"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25 }}
              >
                <p className="text-sm text-[#1a1a1a] font-medium leading-snug mb-3">
                  {simpleHeadline}
                </p>
                <a
                  href={`https://wa.me/33745522450?text=${encodeURIComponent(simpleWhatsappText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-center text-sm font-bold py-2.5 rounded-xl shadow transition-all"
                  data-testid="floating-avatar-whatsapp"
                >
                  💬 Écrire à Sabril
                </a>
                <p className="text-center text-[10px] text-[#1a1a1a]/40 italic mt-2">
                  Aucun engagement · pas de pitch commercial
                </p>
              </motion.div>
            )}

            {/* STEPS — Qualifieur complet (mode par défaut) */}
            {!simpleMode && (
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.25 }}
                >
                  <p className="text-sm text-[#1a1a1a] font-medium leading-snug mb-3">
                    Salut ! 👋 Réponds à <strong>3 questions</strong> — je te dis quelle offre est faite pour toi.
                  </p>
                  <button
                    onClick={() => setStep(1)}
                    className="w-full bg-gradient-to-r from-[#F05A1A] to-[#FF7A3D] text-white text-sm font-bold py-2.5 rounded-xl shadow hover:shadow-lg transition-all"
                    data-testid="floating-avatar-start"
                  >
                    Démarrer le qualifieur →
                  </button>
                  <a
                    href="https://wa.me/33745522450?text=Salut%20Sabril%2C%20j%27ai%20une%20question%20sur%20PRSNL"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center text-[11px] text-[#1a1a1a]/55 hover:text-[#F05A1A] mt-2 transition-colors"
                    data-testid="floating-avatar-cta"
                  >
                    Je préfère écrire directement sur WhatsApp →
                  </a>
                </motion.div>
              )}

              {step === 1 && (
                <QualifierStep
                  key="step1"
                  question="Ton CA mensuel actuel ?"
                  options={[
                    { value: '<20k', label: '< 20 000€', emoji: '🟡' },
                    { value: '20-50k', label: '20 - 50 000€', emoji: '🟢' },
                    { value: '50-100k', label: '50 - 100 000€', emoji: '🔥' },
                    { value: '100k+', label: '100 000€ +', emoji: '🚀' },
                  ]}
                  onPick={(v) => pick('ca', v)}
                />
              )}

              {step === 2 && (
                <QualifierStep
                  key="step2"
                  question="Ton objectif principal ?"
                  options={[
                    { value: 'ouvrir', label: 'Ouvrir un nouveau resto', emoji: '🆕' },
                    { value: 'optimiser', label: 'Optimiser ma rentabilité', emoji: '💰' },
                    { value: 'scaler', label: 'Scaler / multi-resto', emoji: '🏢' },
                  ]}
                  onPick={(v) => pick('goal', v)}
                />
              )}

              {step === 3 && (
                <QualifierStep
                  key="step3"
                  question="Ton timing ?"
                  options={[
                    { value: 'now', label: 'Maintenant — je veux démarrer', emoji: '⚡' },
                    { value: '3mois', label: 'D\'ici 3 mois', emoji: '📅' },
                    { value: 'tester', label: 'Tester d\'abord avant de m\'engager', emoji: '🎯' },
                  ]}
                  onPick={(v) => pick('urgency', v)}
                />
              )}

              {step >= 4 && recommendation && (
                <motion.div
                  key="reco"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  data-testid="floating-avatar-reco"
                >
                  <div className="bg-gradient-to-br from-[#F05A1A]/10 to-[#FF7A3D]/5 border border-[#F05A1A]/30 rounded-xl p-3 mb-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] mb-1">
                      🎯 Recommandation
                    </p>
                    <p className="text-base font-black text-[#1a1a1a] leading-tight">
                      {recommendation.title}
                    </p>
                    <p className="text-xs text-[#1a1a1a]/60 mt-0.5">{recommendation.subtitle}</p>
                    <p className="text-[11px] text-[#1a1a1a]/75 mt-2 leading-snug">
                      {recommendation.reason}
                    </p>
                  </div>

                  <button
                    onClick={() => handleRecommendationClick(recommendation)}
                    className="w-full bg-[#1a1a1a] hover:bg-[#3E3E3E] text-white text-sm font-bold py-2.5 rounded-xl shadow transition-all mb-2"
                    data-testid="floating-avatar-reco-cta"
                  >
                    {recommendation.ctaIcon} {recommendation.ctaLabel}
                  </button>

                  <a
                    href={buildWhatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-center text-sm font-bold py-2.5 rounded-xl shadow transition-all"
                    data-testid="floating-avatar-whatsapp"
                  >
                    💬 En parler sur WhatsApp
                  </a>

                  <button
                    onClick={reset}
                    className="block w-full text-center text-[11px] text-[#1a1a1a]/45 hover:text-[#F05A1A] mt-2 transition-colors"
                    data-testid="floating-avatar-reset"
                  >
                    ↻ Refaire le qualifieur
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        className="relative w-16 h-16 rounded-full shadow-[0_10px_30px_rgba(240,90,26,0.5)] focus:outline-none"
        data-testid="floating-avatar-toggle"
        aria-label="Ouvrir le chat Sabril"
      >
        <motion.span
          className="absolute inset-0 rounded-full bg-[#F05A1A]/40"
          animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
          aria-hidden="true"
        />
        <img
          src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png"
          alt="Sabril"
          className="relative w-16 h-16 rounded-full object-cover border-[3px] border-white bg-white"
          draggable={false}
        />
        <span className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
        {!open && (
          <span className="absolute -top-1 -left-1 bg-[#F05A1A] text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center shadow">
            1
          </span>
        )}
      </motion.button>
    </div>
  );
};

// Internal — one qualifier step (question + clickable options)
const QualifierStep = ({ question, options, onPick }) => (
  <motion.div
    initial={{ opacity: 0, x: 8 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -8 }}
    transition={{ duration: 0.22 }}
  >
    <p className="text-sm font-bold text-[#1a1a1a] mb-2.5 leading-snug">{question}</p>
    <div className="space-y-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt.value)}
          className="w-full text-left bg-[#f5f3f0] hover:bg-[#F05A1A] hover:text-white text-[#1a1a1a] text-[13px] font-medium px-3 py-2 rounded-xl border border-[#e0dbd4] hover:border-[#F05A1A] transition-all flex items-center gap-2"
          data-testid={`qualifier-option-${opt.value}`}
        >
          <span className="text-base">{opt.emoji}</span>
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  </motion.div>
);

export default AnimatedAvatar;
